"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import { useActiveStore } from "@/store/activeStore";
import { Skeleton } from "@/components/ui/Skeleton";
import { FadeIn } from "@/components/ui/FadeIn";
import { LifecycleBadge } from "@/components/ui/LifecycleBadge";
import type { LifecycleStage } from "@/types";

/**
 * 갭 분석 카드 (사용자 결정 2026-05-08, 1순위).
 *
 * 본인 매장 메뉴 vs 활성 트렌드 키워드 비교 → 도입 추천.
 *  - 카테고리: 음료/베이커리/디저트 3종 (매장 4종 중 기타 제외)
 *  - 매칭: alias 정규화 후 정확 일치만
 *  - 정렬: 매장 부재 카테고리 우선 + 점수 percentile asc
 *  - 예상 마진: 본인 매장 카테고리별 평균가 × 카테고리별 마진율
 *
 * 4 분기 UX:
 *   비로그인     → 카드 숨김
 *   매장 미등록  → 등록 유도 CTA
 *   메뉴 0건    → 메뉴 등록 유도 CTA
 *   정상         → 추천 카드 N개
 */

interface GapMenu {
  keyword_id: number | null;
  keyword: string;
  category: string;
  stage: LifecycleStage | null;
  score_percentile: number;
  m30: number;
  supplier_count: number;
}

interface GapMatched {
  keyword: string;
  category: string;
}

interface GapAnalysisOut {
  store_id: number;
  store_name: string;
  menus_by_category: Record<string, number>;
  matched_existing: GapMatched[];
  recommended: GapMenu[];
  secondary?: GapMenu[];   // 5/10 추가 — 다른 후보 (메인 다음 N), #5 옵션 B (루즈함 방지)
  has_menus: boolean;
}

// "왜 추천?" 라인 폐기 (사용자 결정 2026-05-10 — 텍스트 과다 난잡, 다른 후보 칩만 유지)

const CATEGORY_EMOJI: Record<string, string> = {
  음료: "☕",
  베이커리: "🥐",
  디저트: "🍰",
  기타: "📦",
};

interface StoreListOut {
  items: { id: number; name: string }[];
}

interface Props {
  /** true 시 외곽 section 래퍼 제거 — 부모 (MyStoreGroupSection 등) 가 padding/spacing 제어 */
  compact?: boolean;
}

export function GapAnalysisCard({ compact = false }: Props = {}) {
  const { token, isLoggedIn } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const { activeStoreId, setActiveStoreId } = useActiveStore();

  const Wrap = ({ children }: { children: React.ReactNode }) =>
    compact ? <>{children}</> : <section className="px-4 mt-6">{children}</section>;

  const [data, setData] = useState<GapAnalysisOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<"no_store" | "fetch_error" | null>(null);

  useEffect(() => {
    if (!hasHydrated) return;
    if (!isLoggedIn || !token) {
      setData(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };

    // 매장 리스트 조회 → 매장 0개 = no_store / ≥1 = 첫 매장 fetch
    const resolveAndFetch = () => {
      setLoading(true);
      axios
        .get<StoreListOut>("/api/stores", { headers })
        .then((r) => {
          if (cancelled) return;
          if (!r.data.items || r.data.items.length === 0) {
            setActiveStoreId(null);   // 옛 잔존 id 제거
            setError("no_store");
            setLoading(false);
            return;
          }
          const firstId = r.data.items[0].id;
          setActiveStoreId(firstId);
          fetchGap(firstId);
        })
        .catch(() => {
          if (!cancelled) {
            setError("fetch_error");
            setLoading(false);
          }
        });
    };

    const fetchGap = (storeId: number) => {
      setLoading(true);
      setError(null);
      axios
        .get<GapAnalysisOut>(`/api/stores/${storeId}/gap-analysis`, { headers })
        .then((r) => {
          if (!cancelled) {
            setData(r.data);
            setLoading(false);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          // 404/403 = 옛 zustand activeStoreId 잔존 → 매장 리스트 재조회 fallback
          const status = err?.response?.status;
          if (status === 404 || status === 403) {
            resolveAndFetch();
          } else {
            setError("fetch_error");
            setLoading(false);
          }
        });
    };

    if (activeStoreId) {
      fetchGap(activeStoreId);
    } else {
      resolveAndFetch();
    }

    return () => {
      cancelled = true;
    };
  }, [hasHydrated, isLoggedIn, token, activeStoreId, setActiveStoreId]);

  if (!hasHydrated) return null;

  // 비로그인 / 매장 미등록 — 키워드명만 blur (사용자 결정 2026-05-10 재디자인)
  if (!isLoggedIn || error === "no_store") {
    const isAuth = isLoggedIn;
    const ctaHref = isAuth ? "/store" : "/login";
    const ctaLabel = isAuth ? "매장 등록 →" : "로그인 →";
    const lockMessage = isAuth
      ? "매장 등록하면 내 메뉴와 트렌드 비교"
      : "로그인하면 내 메뉴에 어울릴 트렌드 추천";
    return (
      <Wrap>
        <div className="flex items-center justify-between mb-1">
          <h2 className="section-title mb-0">내 메뉴에 어울릴 트렌드 추천</h2>
          <Link href={ctaHref} className="text-xs text-primary font-semibold">{ctaLabel}</Link>
        </div>
        <FadeIn className="card">
          <div className="mb-3 pb-3 border-b border-border">
            <p className="text-xs text-text leading-relaxed mb-1">
              🔒 {lockMessage}
            </p>
            <p className="text-2xs text-text-sub">
              매장에 <strong>부족한 카테고리</strong>를 트렌드 메뉴로 추천해드려요.
            </p>
          </div>
          <div className="space-y-2">
            {[
              { name: "프렌치토스트", category: "베이커리", m30: 31, supplier: 1 },
              { name: "소금빵", category: "베이커리", m30: 55, supplier: 6 },
              { name: "우베크림빵", category: "베이커리", m30: 22, supplier: 0 },
            ].map((rec, i) => (
              <div key={i} className="flex items-center gap-2.5 py-2 px-1">
                <div className="w-7 h-7 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center text-xs shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-text">
                    <span className="blur-sm select-none">{rec.name}</span>
                    <span className="text-2xs ml-1 text-emerald-500">성장기</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </FadeIn>
      </Wrap>
    );
  }

  // 로딩
  if (loading || (!data && !error)) {
    return (
      <Wrap>
        <h2 className="section-title mb-3">내 메뉴에 어울릴 트렌드</h2>
        <FadeIn className="card">
          <div className="text-xs text-text-sub text-center py-1 mb-2">
            🤖 매장 메뉴 ↔ 트렌드 갭 AI 분석 중... 10~15초 정도 소요됩니다
          </div>
          <Skeleton className="h-4 w-40 mb-2" />
          <Skeleton className="h-12 w-full mb-2" />
          <Skeleton className="h-12 w-full" />
        </FadeIn>
      </Wrap>
    );
  }

  // fetch 실패 — 조용히 숨김 (운영 노출 X, dev 디버깅용 console)
  if (error === "fetch_error" || !data) return null;

  // 메뉴 0건 — 키워드명만 blur (사용자 결정 2026-05-10 재디자인)
  if (!data.has_menus) {
    return (
      <Wrap>
        <div className="flex items-center justify-between mb-1">
          <h2 className="section-title mb-0">내 메뉴에 어울릴 트렌드 추천</h2>
          <Link href="/store/menus" className="text-xs text-primary font-semibold">메뉴 등록 →</Link>
        </div>
        <FadeIn className="card">
          <div className="mb-3 pb-3 border-b border-border">
            <p className="text-xs text-text leading-relaxed mb-1">
              🔒 메뉴 등록하면 내 메뉴와 트렌드 비교 추천
            </p>
            <p className="text-2xs text-text-sub">
              매장에 <strong>부족한 카테고리</strong>를 트렌드 메뉴로 추천해드려요.
            </p>
          </div>
          <div className="space-y-2">
            {[
              { name: "프렌치토스트", category: "베이커리", m30: 31, supplier: 1 },
              { name: "소금빵", category: "베이커리", m30: 55, supplier: 6 },
              { name: "우베크림빵", category: "베이커리", m30: 22, supplier: 0 },
            ].map((rec, i) => (
              <div key={i} className="flex items-center gap-2.5 py-2 px-1">
                <div className="w-7 h-7 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center text-xs shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-text">
                    <span className="blur-sm select-none">{rec.name}</span>
                    <span className="text-2xs ml-1 text-emerald-500">성장기</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </FadeIn>
      </Wrap>
    );
  }

  // 정상 — 추천 N개 (5/11 — totalMenus·categorySummary 제거, missingCats 만 사용)
  const missingCats = (["음료", "베이커리", "디저트"] as const).filter(
    (c) => !data.menus_by_category[c],
  );

  return (
    <Wrap>
      <div className="flex items-center justify-between mb-1">
        <h2 className="section-title mb-0">내 메뉴에 어울릴 트렌드 추천</h2>
        <Link
          href="/store/menus"
          className="text-xs text-text-sub hover:text-text transition-colors"
        >
          내 메뉴 →
        </Link>
      </div>

      <FadeIn className="card">
        {/* 설명 라인 제거 (사용자 결정 2026-05-14) — 자리 절약, 상세는 /trends 페이지에서 */}
        {data.recommended.length === 0 ? (
          <p className="text-xs text-text-sub py-3 text-center">
            현재 강세 트렌드와 이미 잘 맞는 메뉴 구성이에요. 새 트렌드가 떠오르면
            여기서 알려드릴게요.
          </p>
        ) : (
          <div className="space-y-2">
            {/* 컴팩트 (사용자 결정 2026-05-14) — TOP 2 만 표시, 나머지는 더보기 */}
            {data.recommended.slice(0, 2).map((rec, i) => {
              const stageBadge = rec.stage ? (
                <LifecycleBadge stage={rec.stage} size="sm" />
              ) : null;
              const inner = (
                <div className="flex items-center gap-2.5 py-2.5 px-1 -mx-1 hover:bg-bg-elevated rounded-lg transition-colors cursor-pointer">
                  {/* 5/11 #9 fix — 이모지 → 번호 (우선순위 명확) */}
                  <div className="w-7 h-7 rounded-full bg-primary/15 text-primary font-bold flex items-center justify-center text-xs shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="text-sm font-bold text-text truncate">
                        {rec.keyword}
                      </span>
                      {stageBadge}
                    </div>
                  </div>
                  <span className="text-2xs font-bold text-primary shrink-0">
                    상세 →
                  </span>
                </div>
              );
              return rec.keyword_id ? (
                <Link key={i} href={`/trend/${rec.keyword_id}`}>
                  {inner}
                </Link>
              ) : (
                <div key={i}>{inner}</div>
              );
            })}

            {/* 더보기 — 추천 키워드만 필터된 페이지 (사용자 결정 2026-05-14) */}
            {(data.recommended.length > 2 || (data.secondary && data.secondary.length > 0)) && (
              <Link
                href="/trends?recommended=1"
                className="block text-center text-2xs font-bold text-primary border border-primary/30 rounded-md py-2 hover:bg-primary/5 transition-colors"
              >
                트렌드 더보기 ({data.recommended.length - 2 + (data.secondary?.length ?? 0)}개) →
              </Link>
            )}
          </div>
        )}
      </FadeIn>
    </Wrap>
  );
}
